import type { SqlColumnMetadata, SqlQueryResults } from '@/services/types';
import type { VisualizationTypeAndConfiguration } from '@/services/visualization';
import type { Effects, InvokeResult, ReduxSagaModel, ReduxSagaEffect } from './types';
import type { Filter } from './objects/datasetViewState';
import type { Task } from 'redux-saga';
import type { CacheParams, CacheRequestType } from '@/services/queryCache/types';
import { VisualizationType } from '@/api';
import Dataset from '@/services/dataset';
import Visualization from '@/services/visualization';
import VisualizationDashboardWidget from '@/services/dashboardWidget/visualizationDashboardWidget';
import { sgcall, sgselect, sgdelay } from '@/utils/reduxSaga';
import { handleUserError } from '@/utils/handleError';
import {
  cancelRedshiftQueryAndIgnoreError,
  waitForRedshiftQueryUntilDoneAndReturnResults,
} from '@/services/dataset.common';
import { DEFAULT_VISUALIZATION_LIMIT } from '@/utils/constants';
import { temporarilyHandleReduxEffectError } from '@/utils/temporarilyHandleReduxEffectError';
import { assertIsNumber } from '@/utils/typeChecks';
import { computeConfigVersion } from '@/utils/configVersion';
import { createConcurrencyLimiter, ConcurrencyLimiterSuperseded } from '@/utils/concurrencyLimiter';
import * as Sentry from '@sentry/react';

const ns = 'app.models.visualization';
const vizQueryLimiter = createConcurrencyLimiter(5);

/** Exposed for diagnostics — allows components to read limiter state without coupling to the saga. */
export const getVizQueryLimiterStats = () => vizQueryLimiter.getStats();

// Tracks the in-flight forked task per vizId so we can cancel the previous one
// when a new query is dispatched for the same visualization.
const runningQueryTaskByVizId = new Map<string, Task>();

/**
 * A single selectable column within the dynamic-filters modal, grouped per
 * visualization. Sourced from the visualization's Dataset columns, with
 * `value` carrying a `.remote` suffix when the column is not present in the
 * visualization query results.
 */
export type DynamicFilterColumnOption = {
  label: string;
  colName: string;
  value: string;
};

export type DynamicFilterColumnOptionGroup = {
  vizName: string;
  cols: DynamicFilterColumnOption[];
};

type Temp1 = InvokeResult<{
  visualization: Visualization;
  sql?: string;
  cols: SqlColumnMetadata[]; // (not used yet) the column metadata for the result, not the Dataset columns
  rows: SqlQueryResults;
  pageSize: number;
  currentPage: number;
  filters: Filter[];
  totalRowCount?: number; // only for table visualization
  aggregateRowData?: Record<string, number>; // only for table visualization with columnTotals
  customQueryRows?: SqlQueryResults; // only for gauge chart with customQuery
  fromCache?: boolean; // result served from the dashboard query cache
  stale?: boolean; // cached result is stale-but-within-tolerance (a fresh refresh should follow)
  cacheCreatedAt?: number; // epoch minutes — when the cache entry was written (for the staleness tag)
}>;

export type State = {
  visualizations: Visualization[];
  visualizationsInWorkspace: Visualization[];
  invokeRunVisualizationQueryResultByVisualizationId: Record<string, Temp1>;
  querySeqByVisualizationId: Record<string, number>;
  invokeCreateResult: InvokeResult<string> | null;
  invokeUpdateResult: InvokeResult<string> | null;
  invokeDeleteResult: InvokeResult<null> | null;
  invokeDynamicFilterColumnOptionsResult: InvokeResult<DynamicFilterColumnOptionGroup[]> | null;
};

const initialState = {
  visualizations: [],
  visualizationsInWorkspace: [],
  invokeRunVisualizationQueryResultByVisualizationId: {},
  querySeqByVisualizationId: {},
  invokeCreateResult: null,
  invokeUpdateResult: null,
  invokeDeleteResult: null,
  invokeDynamicFilterColumnOptionsResult: null,
};

type SaveVisualizationsAction = {
  type: 'saveVisualizations';
  payload: Pick<State, 'visualizations'>;
};

type SaveVisualizationsInWorkspaceAction = {
  type: 'saveVisualizationsInWorkspace';
  payload: Pick<State, 'visualizationsInWorkspace'>;
};

type SaveInvokeRunVisualizationQueryResultAction = {
  type: 'saveInvokeRunVisualizationQueryResult';
  payload: { visualizationId: string; invokeResult: Temp1 };
};

type IncrementQuerySeqAction = {
  type: 'incrementQuerySeq';
  payload: { visualizationId: string };
};

export type SaveInvokeRunVisualizationQueryResultByVisualizationIdAction = {
  type:
    | 'saveInvokeRunVisualizationQueryResultByVisualizationId'
    | 'visualization/saveInvokeRunVisualizationQueryResultByVisualizationId';
  payload: Pick<State, 'invokeRunVisualizationQueryResultByVisualizationId'>;
};

export type SaveInvokeCreateResultAction = {
  type: 'saveInvokeCreateResult';
  payload: Pick<State, 'invokeCreateResult'>;
};

export type SaveInvokeUpdateResultAction = {
  type: 'saveInvokeUpdateResult';
  payload: Pick<State, 'invokeUpdateResult'>;
};

type SaveInvokeDeleteResultAction = {
  type: 'saveInvokeDeleteResult';
  payload: Pick<State, 'invokeDeleteResult'>;
};

export type SaveInvokeDynamicFilterColumnOptionsResultAction = {
  type:
    | 'saveInvokeDynamicFilterColumnOptionsResult'
    | 'visualization/saveInvokeDynamicFilterColumnOptionsResult';
  payload: Pick<State, 'invokeDynamicFilterColumnOptionsResult'>;
};

export type ComputeDynamicFilterColumnOptionsEffect = {
  type: 'visualization/computeDynamicFilterColumnOptions';
  payload: Record<string, never>;
};

// should use FetchVisualizationsEffect instead of FetchVisualizationsEffectPayload
export type FetchVisualizationsEffectPayload = { record: Dataset | string };

export type FetchVisualizationsEffect = {
  type: 'visualization/fetch';
  payload: FetchVisualizationsEffectPayload;
};

export type FetchVisualizationsInWorkspaceEffect = {
  type: 'visualization/fetchInWorkspace';
  payload: Record<string, unknown>;
};

// should use RunVisualizationQueryEffect instead of RunVisualizationQueryEffectPayload
export type RunVisualizationQueryEffectPayload = {
  visualization: Visualization;
  sql?: string;
  dbViewsQualifiedName?: string;
  pageSize?: number;
  currentPage?: number;
  filters?: Filter[];
  _querySeq?: number;
  /**
   * Dashboard query cache context (Phase 1). Present only for cache-enabled dashboards.
   * When set, the MAIN viz-data query goes through the read-through cache. Carries the
   * dashboard's datasetId/datasetVersion/tolerance and the request semantics.
   */
  cache?: CacheEffectParams;
};

/**
 * Cache-related inputs supplied by the widget. Everything beem-agent needs except the
 * per-query fields (page, filters, configVersion) which the model derives/attaches.
 */
export type CacheEffectParams = {
  requestType: CacheRequestType;
  datasetId: string;
  datasetVersion: number;
  stalenessToleranceMinutes: number;
};

export type RunVisualizationQueryEffect = {
  type: 'visualization/runVisualizationQuery';
  payload: RunVisualizationQueryEffectPayload;
};

export type LoadAndRunVisualizationQueryEffectPayload = {
  visualizationId: string;
  filters?: Filter[];
  /**
   * Dashboard query cache context (Phase 1). When present (cache-enabled dashboard),
   * loadAndRunVisualizationQuery forwards it to the query impl. datasetVersion is
   * resolved from the loaded Dataset inside the effect if the widget doesn't know it.
   */
  cache?: LoadCacheEffectParams;
};

/**
 * Cache inputs the widget can supply without having loaded the Dataset. datasetVersion
 * is optional here because the effect loads the Dataset and can fill it in.
 */
export type LoadCacheEffectParams = {
  requestType: CacheRequestType;
  stalenessToleranceMinutes: number;
};

export type LoadAndRunVisualizationQueryEffect = {
  type: 'visualization/loadAndRunVisualizationQuery';
  payload: LoadAndRunVisualizationQueryEffectPayload;
};

export type CreateVisualizationEffectPayload = {
  dataset: Dataset;
  name: string;
  typeAndConfigurations: VisualizationTypeAndConfiguration;
};

export type UpdateVisualizationEffectPayload = {
  id: string;
  name: string;
  typeAndConfigurations: VisualizationTypeAndConfiguration;
};

/**
 * Core implementation of the visualization query execution. Extracted as a standalone
 * generator so it can be:
 * 1. Called from the `runVisualizationQuery` effect (for direct dispatches like pagination)
 * 2. Forked from `loadAndRunVisualizationQuery` with a trackable Task handle for cancellation
 */
const runVisualizationQueryImpl: ReduxSagaEffect<RunVisualizationQueryEffect['payload']> =
  function* runVisualizationQueryImpl(payload, { put, cancelled, spawn }) {
    const {
      visualization,
      sql,
      dbViewsQualifiedName,
      pageSize = DEFAULT_VISUALIZATION_LIMIT,
      currentPage = 1,
      filters = [],
      _querySeq,
      cache,
    } = payload;

    // The read-through cache only applies to the MAIN viz-data query on page 1.
    // Pagination (page >= 2), and the auxiliary sub-queries (column discovery, row
    // count, aggregate totals, gauge custom), always go straight to Redshift.
    const useCache = !!cache && currentPage === 1;

    let executionId: string | null = null;
    const onSuccess = (id: string) => {
      executionId = id;
    };

    // Acquire a concurrency slot. The slot must be released exactly once in the finally
    // below, whether we were granted the slot, superseded, or cancelled while still queued.
    // `slot.release()` is cancellation-safe: releasing a still-queued slot removes it from
    // the limiter's queue rather than leaking an active slot.
    const slot = vizQueryLimiter.acquire(visualization.id);
    try {
      try {
        yield* sgcall(() => slot.granted);
      } catch (e) {
        if (e instanceof ConcurrencyLimiterSuperseded) {
          Sentry.addBreadcrumb({
            category: 'viz-query',
            message: `Query superseded (vizId=${visualization.id}, seq=${_querySeq})`,
            level: 'info',
            data: { vizId: visualization.id, reason: 'superseded', _querySeq },
          });
          return;
        }
        throw e;
      }

      let sqlQuery = sql;
      let isSqlEditorLimitEnabled = false;
      if (!sqlQuery) {
        sqlQuery = yield* sgselect((s) => s.dataset.sqlQuery, 'BEEM250117074115');
        isSqlEditorLimitEnabled = yield* sgselect((s) => s.dataset.isSqlEditorLimitEnabled);
      }
      const { vizQuery, wrapperQuery, aggregateWrapperQuery, displayColumns } =
        visualization.getVisualizationQuery(sqlQuery, isSqlEditorLimitEnabled);

      // Run the MAIN viz-data query. On a cache-enabled dashboard (page 1), this goes
      // through the read-through cache and may return an inline hit (no polling).
      let rows: SqlQueryResults;
      let fromCache = false;
      let stale = false;
      let cacheCreatedAt: number | undefined = undefined;
      // Column metadata for the result. beem-agent returns `cols` alongside the rows
      // (both on an inline cache hit and on a FINISHED poll), so we never need a
      // separate Redshift round-trip just to discover the columns.
      let resultCols: SqlColumnMetadata[] = [];

      if (useCache && cache) {
        const cacheParams: CacheParams = {
          vizId: visualization.id,
          remoteFilters: filters,
          datasetId: cache.datasetId,
          datasetVersion: cache.datasetVersion,
          configVersion: computeConfigVersion(visualization.type, visualization.configurations),
          stalenessToleranceMinutes: cache.stalenessToleranceMinutes,
          requestType: cache.requestType,
          page: currentPage,
        };
        const startResult = yield* sgcall(() =>
          Dataset.testSqlQueryWithCache(
            vizQuery,
            pageSize,
            (currentPage - 1) * pageSize,
            filters,
            cacheParams,
            wrapperQuery,
            displayColumns,
            onSuccess,
          ),
        );
        if (startResult.kind === 'inline') {
          rows = startResult.rows;
          fromCache = true;
          stale = startResult.stale;
          cacheCreatedAt = startResult.createdAt;
          resultCols = startResult.cols;
        } else {
          const result = yield* sgcall(() =>
            waitForRedshiftQueryUntilDoneAndReturnResults({
              executionId: startResult.executionId,
              cacheContext: startResult.cacheContext,
            }),
          );
          rows = result.rows;
          resultCols = result.cols;
        }
      } else {
        const startResult = yield* sgcall(() =>
          Dataset.testSqlQuery(
            vizQuery,
            pageSize,
            (currentPage - 1) * pageSize,
            filters,
            [],
            onSuccess,
            wrapperQuery,
            displayColumns,
          ),
        );
        const result = yield* sgcall(() =>
          waitForRedshiftQueryUntilDoneAndReturnResults(startResult),
        );
        rows = result.rows;
        resultCols = result.cols;
      }

      let totalRowCount: number | undefined = undefined;
      if (visualization.type === VisualizationType.TABLE) {
        if (isSqlEditorLimitEnabled) {
          totalRowCount = rows.length;
        } else {
          const rowCountExecutionId = yield* sgcall(() =>
            Dataset.testSqlQuery(vizQuery, 0, 0, filters, [], onSuccess, wrapperQuery),
          );
          const { rows: rowCountResponse } = yield* sgcall(() =>
            waitForRedshiftQueryUntilDoneAndReturnResults(rowCountExecutionId),
          );
          const temp = rowCountResponse[0]?.count;
          assertIsNumber(temp, 'BEEM250121152530');
          totalRowCount = temp;
        }
      }

      let aggregateRowData: Record<string, number> | undefined = undefined;
      if (aggregateWrapperQuery && !isSqlEditorLimitEnabled) {
        const totalsExecutionId = yield* sgcall(() =>
          Dataset.testSqlQuery(
            vizQuery,
            2_147_483_647,
            0,
            filters,
            [],
            onSuccess,
            aggregateWrapperQuery,
          ),
        );
        const { rows: totalsResponse } = yield* sgcall(() =>
          waitForRedshiftQueryUntilDoneAndReturnResults(totalsExecutionId),
        );
        if (totalsResponse.length > 0) {
          aggregateRowData = Object.fromEntries(
            Object.entries(totalsResponse[0]).map(([k, v]) => [
              k,
              typeof v === 'number' ? v : parseFloat(String(v)),
            ]),
          );
        }
      }

      // Run custom query for gauge charts if configured
      let customQueryRows: SqlQueryResults | undefined = undefined;
      if (visualization.type === VisualizationType.GAUGE_CHART && dbViewsQualifiedName) {
        const gaugeConfig = visualization.configurations as { customQuery?: string };
        const rawCustomQuery = gaugeConfig.customQuery;
        if (rawCustomQuery && rawCustomQuery.trim()) {
          const resolvedCustomQuery = rawCustomQuery.replace(/\{\{data\}\}/g, dbViewsQualifiedName);
          const customQueryExecutionId = yield* sgcall(() =>
            Dataset.testSqlQuery(resolvedCustomQuery, 1000, 0, [], [], onSuccess),
          );
          ({ rows: customQueryRows } = yield* sgcall(() =>
            waitForRedshiftQueryUntilDoneAndReturnResults(customQueryExecutionId),
          ));
        }
      }

      // Request sequencing: if a newer query was started for this vizId while we
      // were running, our result is stale — discard it to prevent overwriting a
      // fresher result (or a result that will arrive shortly from the newer saga).
      const currentSeq: number = yield* sgselect(
        (s) => s.visualization.querySeqByVisualizationId[visualization.id] || 0,
      );
      if (_querySeq !== undefined && currentSeq !== _querySeq) {
        Sentry.addBreadcrumb({
          category: 'viz-query',
          message: `Stale result discarded on success (vizId=${visualization.id})`,
          level: 'info',
          data: { vizId: visualization.id, reason: 'stale-seq-success', _querySeq, currentSeq },
        });
        return; // stale — a newer request has been started
      }

      yield put<SaveInvokeRunVisualizationQueryResultAction>({
        type: 'saveInvokeRunVisualizationQueryResult',
        payload: {
          visualizationId: visualization.id,
          invokeResult: {
            success: true,
            data: {
              visualization,
              sql,
              cols: resultCols,
              rows,
              pageSize,
              currentPage,
              filters,
              totalRowCount,
              aggregateRowData,
              customQueryRows,
              fromCache,
              stale,
              cacheCreatedAt,
            },
          },
        },
      });
    } catch (e) {
      // On error, still check the seq — no point showing an error for a stale request
      // when a newer request is in flight.
      const currentSeq: number = yield* sgselect(
        (s) => s.visualization.querySeqByVisualizationId[visualization.id] || 0,
      );
      if (_querySeq !== undefined && currentSeq !== _querySeq) {
        Sentry.addBreadcrumb({
          category: 'viz-query',
          message: `Stale error discarded (vizId=${visualization.id})`,
          level: 'info',
          data: {
            vizId: visualization.id,
            reason: 'stale-seq-error',
            _querySeq,
            currentSeq,
            error: String(e),
          },
        });
        return; // stale
      }

      yield put<SaveInvokeRunVisualizationQueryResultAction>({
        type: 'saveInvokeRunVisualizationQueryResult',
        payload: {
          visualizationId: visualization.id,
          invokeResult: {
            success: false,
            error: handleUserError(e, `${ns}.runVisualizationQuery.error`),
          },
        },
      });
    } finally {
      slot.release();
      runningQueryTaskByVizId.delete(visualization.id);
      if (yield cancelled()) {
        yield spawn(function* cancelQueryTask() {
          // Wait up to 30 seconds for executionId to be set. If it's never set
          // (task cancelled before testSqlQuery returned), give up — there's no
          // Redshift query to cancel.
          const MAX_CANCEL_WAIT_ITERATIONS = 30;
          let i = 0;
          while (!executionId && i < MAX_CANCEL_WAIT_ITERATIONS) {
            yield* sgcall(() => new Promise((resolve) => setTimeout(resolve, 1000)));
            i++;
          }
          if (executionId) {
            yield* sgcall(() => cancelRedshiftQueryAndIgnoreError({ executionId: executionId! }));
          }
        });
      }
    }
  };

const VisualizationModel: ReduxSagaModel<
  State,
  {
    saveVisualizations: SaveVisualizationsAction;
    saveVisualizationsInWorkspace: SaveVisualizationsInWorkspaceAction;
    saveInvokeRunVisualizationQueryResult: SaveInvokeRunVisualizationQueryResultAction;
    incrementQuerySeq: IncrementQuerySeqAction;
    saveInvokeRunVisualizationQueryResultByVisualizationId: SaveInvokeRunVisualizationQueryResultByVisualizationIdAction;
    saveInvokeCreateResult: SaveInvokeCreateResultAction;
    saveInvokeUpdateResult: SaveInvokeUpdateResultAction;
    saveInvokeDeleteResult: SaveInvokeDeleteResultAction;
    saveInvokeDynamicFilterColumnOptionsResult: SaveInvokeDynamicFilterColumnOptionsResultAction;
  }
> = {
  namespace: 'visualization',
  state: initialState,
  effects: {
    *fetch({ payload }: { payload: FetchVisualizationsEffect['payload'] }, { put }) {
      try {
        const temp1 = payload.record;
        const dataset =
          typeof temp1 === 'string' ? yield* sgcall(() => Dataset.getDatasetById(temp1)) : temp1;
        const visualizations = yield* sgcall(dataset.getVisualizations);
        yield put<SaveVisualizationsAction>({
          type: 'saveVisualizations',
          payload: { visualizations },
        });
      } catch (e) {
        yield put<SaveVisualizationsAction>({
          type: 'saveVisualizations',
          payload: { visualizations: [] },
        });
        temporarilyHandleReduxEffectError(e, `${ns}.fetch.error`);
      }
    },
    *fetchInWorkspace(
      _ /* { payload }: { payload: FetchVisualizationsInWorkspaceEffect['payload'] } */,
      { put },
    ) {
      try {
        const workspaceId = yield* sgselect(
          (s) => s.organization.currentWorkspace,
          'BEEM250325153546',
        );
        const visualizations = yield* sgcall(() =>
          Visualization.getVisualizationsByWorkspace(workspaceId),
        );
        yield put<SaveVisualizationsInWorkspaceAction>({
          type: 'saveVisualizationsInWorkspace',
          payload: { visualizationsInWorkspace: visualizations },
        });
      } catch (e) {
        yield put<SaveVisualizationsInWorkspaceAction>({
          type: 'saveVisualizationsInWorkspace',
          payload: { visualizationsInWorkspace: [] },
        });
        temporarilyHandleReduxEffectError(e, `${ns}.fetchInWorkspace.error`);
      }
    },
    *runVisualizationQuery(
      { payload }: { payload: RunVisualizationQueryEffect['payload'] },
      effects: Effects,
    ) {
      yield* runVisualizationQueryImpl(payload, effects);
    },
    *loadAndRunVisualizationQuery(
      { payload }: { payload: LoadAndRunVisualizationQueryEffect['payload'] },
      effects: Effects,
    ) {
      const { cancel, fork, put } = effects;
      try {
        // Cancel any previously in-flight query task for the same vizId. This triggers
        // the `if (yield cancelled())` block in runVisualizationQueryImpl, which cancels
        // the Redshift query and releases the concurrency limiter slot.
        const previousTask = runningQueryTaskByVizId.get(payload.visualizationId);
        if (previousTask?.isRunning()) {
          yield cancel(previousTask);
          runningQueryTaskByVizId.delete(payload.visualizationId);
        }

        // Increment the sequence counter for this vizId (Option A safety net).
        yield put<IncrementQuerySeqAction>({
          type: 'incrementQuerySeq',
          payload: { visualizationId: payload.visualizationId },
        });
        const mySeq: number = yield* sgselect(
          (s) => s.visualization.querySeqByVisualizationId[payload.visualizationId] || 0,
        );

        const visualization = yield* sgcall(() =>
          Visualization.getVisualizationById(payload.visualizationId),
        );
        const dataset = yield* sgcall(() => Dataset.getDatasetById(visualization.datasetId));
        const sql = `SELECT * FROM ${dataset.dbViewsQualifiedName}`;

        // If the dashboard has caching enabled, resolve the datasetVersion (needed for the
        // freshness check) from the just-loaded Dataset and build the cache context. When
        // payload.cache is absent (cache off), the query runs uncached, exactly as before.
        const cache: RunVisualizationQueryEffectPayload['cache'] = payload.cache
          ? {
              requestType: payload.cache.requestType,
              datasetId: dataset.id,
              datasetVersion: dataset.version,
              stalenessToleranceMinutes: payload.cache.stalenessToleranceMinutes,
            }
          : undefined;

        // Fork the query execution as a trackable task. Using fork (not put) ensures
        // we get a Task handle that can be cancelled on the next filter change.
        // This is the key difference from dispatching runVisualizationQuery as an action —
        // the forked task contains the entire long-running execution and responds to cancel().
        // Note: fork() returns the Task handle synchronously (no yield suspension between
        // getting the handle and storing it), so there's no cancellation window here.
        const task = (yield fork(
          runVisualizationQueryImpl,
          {
            visualization,
            sql,
            dbViewsQualifiedName: dataset.dbViewsQualifiedName,
            filters: payload.filters,
            _querySeq: mySeq,
            cache,
          },
          effects,
        )) as Task;
        runningQueryTaskByVizId.set(payload.visualizationId, task);
      } catch (e) {
        yield put<SaveInvokeRunVisualizationQueryResultAction>({
          type: 'saveInvokeRunVisualizationQueryResult',
          payload: {
            visualizationId: payload.visualizationId,
            invokeResult: {
              success: false,
              error: handleUserError(e, `${ns}.loadAndRunVisualizationQuery.error`),
            },
          },
        });
      }
    },
    *create({ payload }: { payload: CreateVisualizationEffectPayload }, { put }) {
      try {
        const newVizId = yield* sgcall(() => Visualization.create(payload));
        yield put<SaveInvokeCreateResultAction>({
          type: 'saveInvokeCreateResult',
          payload: { invokeCreateResult: { success: true, data: newVizId } },
        });
      } catch (e) {
        yield put<SaveInvokeCreateResultAction>({
          type: 'saveInvokeCreateResult',
          payload: {
            invokeCreateResult: {
              success: false,
              error: handleUserError(e, `${ns}.invokeCreate.error`),
            },
          },
        });
      }
    },
    *update({ payload }: { payload: UpdateVisualizationEffectPayload }, { put }) {
      try {
        const vizId = yield* sgcall(() => Visualization.update(payload));
        yield put<SaveInvokeUpdateResultAction>({
          type: 'saveInvokeUpdateResult',
          payload: { invokeUpdateResult: { success: true, data: vizId } },
        });
      } catch (e) {
        yield put<SaveInvokeUpdateResultAction>({
          type: 'saveInvokeUpdateResult',
          payload: {
            invokeUpdateResult: {
              success: false,
              error: handleUserError(e, `${ns}.invokeUpdate.error`),
            },
          },
        });
      }
    },
    *invokeDelete({ payload }: { payload: { id: string } }, { put }) {
      try {
        yield* sgcall(() => Visualization.delete(payload));
        yield put<SaveInvokeDeleteResultAction>({
          type: 'saveInvokeDeleteResult',
          payload: { invokeDeleteResult: { success: true, data: null } },
        });
      } catch (e) {
        yield put<SaveInvokeDeleteResultAction>({
          type: 'saveInvokeDeleteResult',
          payload: {
            invokeDeleteResult: {
              success: false,
              error: handleUserError(e, `${ns}.invokeDelete.error`),
            },
          },
        });
      }
    },
    /**
     * Build the per-visualization column options shown in the dynamic-filters modal.
     *
     * The selectable columns come from each visualization's *Dataset* columns (loaded
     * via Dataset.getDatasetById), not from the visualization query result. The query
     * result's `cols` are only used to derive `isRemote`: a Dataset column that is not
     * present in the query result is remote (kept, flagged with a `.remote` suffix in
     * `value`); query-result columns absent from the Dataset are dropped (implicit,
     * since we iterate Dataset columns). Each visualization's Dataset must be found and
     * must expose columns — otherwise the effect fails with a user-facing error.
     */
    *computeDynamicFilterColumnOptions(_: ComputeDynamicFilterColumnOptionsEffect, { put }) {
      try {
        const widgets = yield* sgselect((s) => s.dashboard.currentWidgets);

        const vizWidgets = widgets.filter(
          (w): w is VisualizationDashboardWidget => w instanceof VisualizationDashboardWidget,
        );
        const vizIds = vizWidgets.map((w) => w.getConfigurations().visualizationId);

        // Wait for any in-flight query for these visualizations to finish so we read a
        // complete set of results (and not a snapshot taken mid-fetch). Bounded so a
        // stuck query can't hang the modal forever; on timeout we compute from whatever
        // results are available.
        //
        // Known limitation: this is a bounded poll that holds a saga slot for up to ~30s.
        // If many widgets open the dynamic-filters modal at once, several of these can wait
        // concurrently and contend for saga slots. We use the `delay` effect (via sgdelay)
        // rather than a raw Promise-wrapped setTimeout so redux-saga can cancel the wait if
        // the task is taken over, releasing the slot promptly instead of always running the
        // full interval.
        const WAIT_INTERVAL_MS = 200;
        const MAX_WAIT_ITERATIONS = 150; // ~30s
        let i = 0;
        while (
          i < MAX_WAIT_ITERATIONS &&
          vizIds.some((id) => runningQueryTaskByVizId.get(id)?.isRunning())
        ) {
          yield* sgdelay(WAIT_INTERVAL_MS);
          i++;
        }

        const resultByVizId = yield* sgselect(
          (s) => s.visualization.invokeRunVisualizationQueryResultByVisualizationId,
        );

        // Multiple visualizations can share the same Dataset, so cache each loaded
        // Dataset by id and fetch it at most once.
        const datasetById = new Map<string, Dataset>();

        const groups: DynamicFilterColumnOptionGroup[] = [];
        for (const widget of vizWidgets) {
          const vizId = widget.getConfigurations().visualizationId;
          const invokeResult = resultByVizId[vizId];
          // Only visualizations with a successful query result contribute options — the
          // result carries the visualization (name + datasetId) and the `cols` needed to
          // compute isRemote. Skip the rest, matching the previous behaviour.
          if (!invokeResult?.success) continue;

          const { visualization, cols } = invokeResult.data;

          let dataset = datasetById.get(visualization.datasetId);
          if (!dataset) {
            dataset = yield* sgcall(() => Dataset.getDatasetById(visualization.datasetId));
            datasetById.set(visualization.datasetId, dataset);
          }
          const datasetColumns = dataset.columns;
          if (!datasetColumns || datasetColumns.length === 0) {
            throw new Error(`BEEM260828120000|${visualization.datasetId}`);
          }

          const resultColNames = new Set(cols.map((c) => c.name));
          groups.push({
            vizName: visualization.name,
            cols: datasetColumns.map((col) => {
              const isRemote = !resultColNames.has(col.id);
              return {
                label: `${visualization.name} / ${col.id}`,
                colName: col.id,
                value: `${visualization.id}.${col.id}` + (isRemote ? '.remote' : ''),
              };
            }),
          });
        }

        yield put<SaveInvokeDynamicFilterColumnOptionsResultAction>({
          type: 'saveInvokeDynamicFilterColumnOptionsResult',
          payload: {
            invokeDynamicFilterColumnOptionsResult: { success: true, data: groups },
          },
        });
      } catch (e) {
        yield put<SaveInvokeDynamicFilterColumnOptionsResultAction>({
          type: 'saveInvokeDynamicFilterColumnOptionsResult',
          payload: {
            invokeDynamicFilterColumnOptionsResult: {
              success: false,
              error: handleUserError(e, `${ns}.computeDynamicFilterColumnOptions.error`),
            },
          },
        });
      }
    },
  },
  reducers: {
    resetAll() {
      return { ...initialState };
    },
    saveVisualizations(state, { payload }) {
      return { ...state, ...payload };
    },
    saveVisualizationsInWorkspace(state, { payload }) {
      return { ...state, ...payload };
    },
    saveInvokeRunVisualizationQueryResult(state, { payload }) {
      return {
        ...state,
        invokeRunVisualizationQueryResultByVisualizationId: {
          ...state.invokeRunVisualizationQueryResultByVisualizationId,
          [payload.visualizationId]: payload.invokeResult,
        },
      };
    },
    incrementQuerySeq(state, { payload }) {
      const current = state.querySeqByVisualizationId[payload.visualizationId] || 0;
      return {
        ...state,
        querySeqByVisualizationId: {
          ...state.querySeqByVisualizationId,
          [payload.visualizationId]: current + 1,
        },
      };
    },
    saveInvokeRunVisualizationQueryResultByVisualizationId(state, { payload }) {
      return { ...state, ...payload };
    },
    saveInvokeCreateResult(state, { payload }) {
      return { ...state, ...payload };
    },
    saveInvokeUpdateResult(state, { payload }) {
      return { ...state, ...payload };
    },
    saveInvokeDeleteResult(state, { payload }) {
      return { ...state, ...payload };
    },
    saveInvokeDynamicFilterColumnOptionsResult(state, { payload }) {
      return { ...state, ...payload };
    },
  },
};

export default VisualizationModel;
